import { translate } from '@teamleader/i18n';
import { IconChevronDownSmallOutline, IconChevronRightSmallOutline } from '@teamleader/ui-icons';
import React, { useCallback, useMemo, useState } from 'react';

import Quotation from './Quotation';
import Box from '../../../../Components/UI/Box';
import { Deal as IDeal } from '../../../../Types/deal';
import { CUSTOMER_PORTAL_STATUS, Quotation as IQuotation } from '../../../../Types/quotation';
import { EVENTS, track } from '../../../../Utils/analytics';
import styles from '../styles.css';

interface DealProps {
  deal: IDeal;
  initiallyCollapsed: boolean;
  onQuotationClick?: (quotation: IQuotation) => void;
}

const Deal: React.FC<DealProps> = ({ deal, initiallyCollapsed, onQuotationClick }) => {
  const [isCollapsed, setIsCollapsed] = useState(initiallyCollapsed);

  const { open, signed } = useMemo(
    () =>
      deal.quotations.reduce<{ open: IQuotation[]; signed: IQuotation[] }>(
        (map, quotation) => {
          if (quotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED) {
            map.signed.push(quotation);
            return map;
          }

          map.open.push(quotation);
          return map;
        },
        { open: [], signed: [] },
      ),
    [deal.quotations],
  );

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);

    if (isCollapsed) {
      track(EVENTS.SELECTED_DEAL, { deal: deal.id });
    }
  }, [isCollapsed, setIsCollapsed, deal.id]);

  const Icon = isCollapsed ? IconChevronRightSmallOutline : IconChevronDownSmallOutline;
  return (
    <>
      <Box
        element="button"
        className={styles['item']}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        onClick={handleToggleCollapse}
      >
        <Icon className={styles['icon']} />
        <Box flex={1} marginLeft={3}>
          {deal.name}
        </Box>
      </Box>
      {!isCollapsed && (
        <Box className={styles['sub-items']}>
          {signed.length > 0 && (
            <>
              <Box element="h3" className={styles['subtitle']}>
                {translate('signedQuotations')}
              </Box>
              <Box>
                {signed.map((quotation) => (
                  <Quotation key={quotation.id} deal={deal} quotation={quotation} onClick={onQuotationClick} />
                ))}
              </Box>
            </>
          )}
          {deal.quotations.length > 1 && (
            <Box element="h3" className={styles['subtitle']}>
              {translate(signed.length > 0 ? 'closedQuotations' : 'openQuotations', {
                amount: open.length,
              })}
            </Box>
          )}
          <Box>
            {open.map((quotation) => (
              <Quotation key={quotation.id} deal={deal} quotation={quotation} onClick={onQuotationClick} />
            ))}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Deal;
